import { useState, useEffect } from 'react'
import { api } from '../../services/api.js'
import PartialDateInput from '../shared/PartialDateInput.jsx'
import PlaceInput from '../shared/PlaceInput.jsx'
import PhotoUpload from './PhotoUpload.jsx'
import { GENDER_OPTIONS } from '../../utils/constants.js'
import './EditPersonPanel.css'

export default function EditPersonPanel({ person, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name:     '',
    preferred_name: '',
    last_name:      '',
    birth_name:     '',
    gender:         '',
    birth_date:     '',
    birth_place:    '',
    death_date:     '',
    death_place:    '',
    cause_of_death: '',
    occupation:     '',
    notes:          '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (person) {
      setForm({
        first_name:     person.first_name     || '',
        preferred_name: person.preferred_name || '',
        last_name:      person.last_name      || '',
        birth_name:     person.birth_name     || '',
        gender:         person.gender         || '',
        birth_date:     person.birth_date     || '',
        birth_place:    person.birth_place    || '',
        death_date:     person.death_date     || '',
        death_place:    person.death_place    || '',
        cause_of_death: person.cause_of_death || '',
        occupation:     person.occupation     || '',
        notes:          person.notes          || '',
      })
    }
  }, [person])

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }))
  }

  async function save(e) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const updated = await api.patch(`/persons/${person.id}`, form)
      onSaved(updated)
      onClose()
    } catch (err) {
      // Shared canonical persons: direct edit is blocked server-side.
      // Point the user at the Suggest-change flow instead of a terse
      // "Failed to save". We don't auto-open the suggestion modal here
      // because the user has a big form with unsaved changes — better
      // to give them a clear message + action.
      if (err.code === 'EDIT_REQUIRES_SUGGESTION') {
        setError(`This person is shared across ${err.meta?.tree_count || 'multiple'} trees — direct edits are disabled. Close this form and click "Suggest change" to propose updates for review.`)
      } else {
        setError(err.message || 'Failed to save')
      }
    } finally {
      setSaving(false)
    }
  }

  async function deletePerson() {
    setDeleting(true)
    try {
      // tree_id is required: delete removes the tree membership only.
      // The person stays alive platform-wide if another tree still has them.
      await api.delete(`/persons/${person.id}?tree_id=${person.tree_id}`)
      onSaved(null) // null signals deletion
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to delete')
      setDeleting(false)
    }
  }

  return (
    <>
      <div className="edit-panel__overlay" onClick={onClose} />
      <div className="edit-panel" role="dialog" aria-label="Edit person">
        <div className="edit-panel__header">
          <div className="edit-panel__title">Edit person</div>
          <button className="edit-panel__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form className="edit-panel__body" onSubmit={save}>
          <div className="edit-section">
            <div className="edit-section__label">Photo</div>
            <PhotoUpload person={person} onUploaded={(updated) => onSaved(updated)} />
          </div>

          <div className="edit-section">
            <div className="edit-section__label">Name</div>
            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">All given names</label>
                <input className="form-input" value={form.first_name}
                  onChange={e => set('first_name', e.target.value)} placeholder="e.g. Johannes Wilhelmus Petrus" />
              </div>
              <div className="form-field">
                <label className="form-field__label">Last name</label>
                <input className="form-input" value={form.last_name}
                  onChange={e => set('last_name', e.target.value)} placeholder="e.g. de Vries" />
              </div>
            </div>
            <div className="form-field" style={{ marginBottom: '0.75rem' }}>
              <label className="form-field__label">
                Preferred name <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(the name they actually used)</span>
              </label>
              <input className="form-input" value={form.preferred_name}
                onChange={e => set('preferred_name', e.target.value)}
                placeholder={form.first_name ? `e.g. ${form.first_name.split(' ')[0]}` : 'e.g. Jan'} />
            </div>

          <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Birth name (maiden)</label>
                <input className="form-input" value={form.birth_name}
                  onChange={e => set('birth_name', e.target.value)} placeholder="if different" />
              </div>
              <div className="form-field">
                <label className="form-field__label">Gender</label>
                <select className="form-input form-input--select" value={form.gender}
                  onChange={e => set('gender', e.target.value)}>
                  {GENDER_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="edit-section">
            <div className="edit-section__label">Birth</div>
            <div className="form-field" style={{ marginBottom: '0.75rem' }}>
              <label className="form-field__label">Birth date</label>
              <PartialDateInput value={form.birth_date} onChange={v => set('birth_date', v)} />
            </div>
            <div className="form-field">
              <label className="form-field__label">Birth place</label>
              <PlaceInput value={form.birth_place}
                onChange={v => set('birth_place', v)} placeholder="e.g. Rotterdam, Netherlands" />
            </div>
          </div>

          <div className="edit-section">
            <div className="edit-section__label">Death</div>
            <div className="form-field" style={{ marginBottom: '0.75rem' }}>
              <label className="form-field__label">Death date</label>
              <PartialDateInput value={form.death_date} onChange={v => set('death_date', v)} />
            </div>
            <div className="form-field" style={{ marginBottom: '0.75rem' }}>
              <label className="form-field__label">Death place</label>
              <PlaceInput value={form.death_place}
                onChange={v => set('death_place', v)} placeholder="e.g. Amsterdam, Netherlands" />
            </div>
            <div className="form-field">
              <label className="form-field__label">Cause of death</label>
              <input className="form-input" value={form.cause_of_death}
                onChange={e => set('cause_of_death', e.target.value)}
                placeholder="e.g. Pneumonia (helps family medical history)" />
            </div>
          </div>

          <div className="edit-section">
            <div className="edit-section__label">Details</div>
            <div className="form-field" style={{ marginBottom: '0.75rem' }}>
              <label className="form-field__label">Occupation</label>
              <input className="form-input" value={form.occupation}
                onChange={e => set('occupation', e.target.value)} placeholder="e.g. Merchant, Fisherman" />
            </div>
            <div className="form-field">
              <label className="form-field__label">Notes</label>
              <textarea className="form-input form-input--textarea" value={form.notes}
                onChange={e => set('notes', e.target.value)}
                placeholder="Sources, additional context, transcribed text..." />
            </div>
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="edit-panel__actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>

        <div className="edit-panel__danger">
          {!confirmDelete ? (
            <button className="edit-panel__delete-btn" onClick={() => setConfirmDelete(true)}>
              Delete person
            </button>
          ) : (
            <div className="edit-panel__delete-confirm">
              <span>This cannot be undone. All relationships will be removed.</span>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button className="btn btn-sm" style={{ background: '#dc2626', color: 'white', border: 'none' }}
                  onClick={deletePerson} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Yes, delete'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
